import {
    InvoiceDebtReminderEmailTemplateType,
    InvoiceDebtReminderScopeType,
    InvoiceDebtReminderType,
} from 'types/invoices';

/**
 * Gets the debt reminder email template type based on the reminder type and scope type.
 *
 * @param debtReminderType - The type of debt reminder (TYPE1, TYPE2, TYPE3, TYPE4, or OVERVIEW)
 * @param debtReminderScopeType - The scope of the debt reminder (SINGLE_INVOICE, ALL_COMPANY_INVOICES, or ALL_INVOICES)
 * @returns The corresponding email template type
 * @throws Error if the combination is not supported
 */
export function getDebtReminderTemplateType(
    debtReminderType: InvoiceDebtReminderType,
    debtReminderScopeType: InvoiceDebtReminderScopeType
): InvoiceDebtReminderEmailTemplateType {
    const templateTypeMap: Record<string, InvoiceDebtReminderEmailTemplateType> = {
        [`${InvoiceDebtReminderScopeType.SINGLE_INVOICE}_${InvoiceDebtReminderType.TYPE1}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_SINGLE_INVOICE_TYPE1,
        [`${InvoiceDebtReminderScopeType.SINGLE_INVOICE}_${InvoiceDebtReminderType.TYPE2}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_SINGLE_INVOICE_TYPE2,
        [`${InvoiceDebtReminderScopeType.SINGLE_INVOICE}_${InvoiceDebtReminderType.TYPE3}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_SINGLE_INVOICE_TYPE3,
        [`${InvoiceDebtReminderScopeType.SINGLE_INVOICE}_${InvoiceDebtReminderType.TYPE4}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_SINGLE_INVOICE_TYPE4,
        [`${InvoiceDebtReminderScopeType.SINGLE_INVOICE}_${InvoiceDebtReminderType.OVERVIEW}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_SINGLE_INVOICE_OVERVIEW,
        [`${InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES}_${InvoiceDebtReminderType.TYPE1}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE1,
        [`${InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES}_${InvoiceDebtReminderType.TYPE2}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE2,
        [`${InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES}_${InvoiceDebtReminderType.TYPE3}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE3,
        [`${InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES}_${InvoiceDebtReminderType.TYPE4}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE4,
        [`${InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES}_${InvoiceDebtReminderType.OVERVIEW}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_COMPANY_INVOICES_OVERVIEW,
        [`${InvoiceDebtReminderScopeType.ALL_INVOICES}_${InvoiceDebtReminderType.TYPE1}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_INVOICES_TYPE1,
        [`${InvoiceDebtReminderScopeType.ALL_INVOICES}_${InvoiceDebtReminderType.TYPE2}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_INVOICES_TYPE2,
        [`${InvoiceDebtReminderScopeType.ALL_INVOICES}_${InvoiceDebtReminderType.TYPE3}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_INVOICES_TYPE3,
        [`${InvoiceDebtReminderScopeType.ALL_INVOICES}_${InvoiceDebtReminderType.TYPE4}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_INVOICES_TYPE4,
        [`${InvoiceDebtReminderScopeType.ALL_INVOICES}_${InvoiceDebtReminderType.OVERVIEW}`]:
            InvoiceDebtReminderEmailTemplateType.DEBT_REMINDER_ALL_INVOICES_OVERVIEW,
    };

    const key = `${debtReminderScopeType}_${debtReminderType}`;
    const templateType = templateTypeMap[key];

    if (!templateType) {
        throw new Error(`Template type not found for combination: ${debtReminderType} + ${debtReminderScopeType}`);
    }

    return templateType;
}
